import React from 'react'
import PropTypes from 'prop-types'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import {
  Heading1,
  Heading2,
  Heading3,
  TextBody,
  TextBodySmall,
  TextBodySmaller,
} from '../TextStyles'
import AnimateSplitText from '../animation/AnimateSplitText'
import {
  ListItem,
  OrderedList,
  RichTextMain,
  UnorderedList,
} from './index.style'
import { GatsbyImage } from 'gatsby-plugin-image'
import PageTransitionLink from '../PageTransitionLink'
import InViewSection from '../InViewSection'

const RichText = ({ content, delay, smallParagraph, smallerParagraph }) => {
  const options = {
    renderMark: {
      [MARKS.BOLD]: text => <strong>{text}</strong>,
      [MARKS.ITALIC]: text => <em>{text}</em>,
      [MARKS.UNDERLINE]: text => <u>{text}</u>,
      [MARKS.CODE]: text => <code>{text}</code>,
    },
    renderNode: {
      /*
       ** Blocks
       */

      [BLOCKS.PARAGRAPH]: (node, children) => {
        if (smallerParagraph) {
          return (
            <TextBodySmaller>
              <AnimateSplitText delay={delay}>{children}</AnimateSplitText>
            </TextBodySmaller>
          )
        } else if (smallParagraph) {
          return (
            <TextBodySmall>
              <AnimateSplitText delay={delay}>{children}</AnimateSplitText>
            </TextBodySmall>
          )
        } else
          return (
            <TextBody>
              <AnimateSplitText delay={delay}>{children}</AnimateSplitText>
            </TextBody>
          )
      },

      [BLOCKS.HEADING_1]: (node, children) => (
        <Heading1>
          <AnimateSplitText delay={delay}>{children}</AnimateSplitText>
        </Heading1>
      ),

      [BLOCKS.HEADING_2]: (node, children) => (
        <Heading2>
          <AnimateSplitText delay={delay}>{children}</AnimateSplitText>
        </Heading2>
      ),

      [BLOCKS.HEADING_3]: (node, children) => (
        <Heading3>
          <AnimateSplitText delay={delay}>{children}</AnimateSplitText>
        </Heading3>
      ),

      [BLOCKS.OL_LIST]: (node, children) => (
        <OrderedList>{children}</OrderedList>
      ),

      [BLOCKS.UL_LIST]: (node, children) => (
        <UnorderedList>{children}</UnorderedList>
      ),

      [BLOCKS.LIST_ITEM]: (node, children) => {
        return (
          <InViewSection>
            <ListItem delay={delay}>{children}</ListItem>
          </InViewSection>
        )
      },

      [BLOCKS.HR]: () => <hr />,

      [BLOCKS.QUOTE]: (node, children) => <blockquote>{children}</blockquote>,

      [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
        const { description, gatsbyImageData } = node.data.target

        return <GatsbyImage image={gatsbyImageData} alt={description} />
      },

      /*
       ** Inlines
       */

      [INLINES.HYPERLINK]: (node, children) => (
        <a href={node.data.uri} target="_blank" rel="noreferrer">
          {children}
        </a>
      ),

      [INLINES.ASSET_HYPERLINK]: (node, children) => (
        <a href={node.data.target.file.url}>{children}</a>
      ),

      [INLINES.ENTRY_HYPERLINK]: (node, children) => (
        <PageTransitionLink to={`/${node.data.target.slug}`}>
          {children}
        </PageTransitionLink>
      ),
    },
    renderText: text =>
      React.Children.toArray(
        text.split('\n').map((t, i) =>
          i > 0 ? (
            <>
              <br />
              {t}
            </>
          ) : (
            t
          )
        )
      ),
  }

  return <RichTextMain>{renderRichText(content, options)}</RichTextMain>
}

RichText.propTypes = {
  content: PropTypes.object,
  delay: PropTypes.number,
}

export default RichText
